import { useIntl } from "react-intl";
import { Dialog } from "@rio-cloud/rio-uikit";
import { useEffect, useState } from "react";
import { fetchUser, IUserProfile } from "../../../../../../api/fetchUser";
import { useSelector } from "react-redux";
import { getUserProfile } from "../../../../../../configuration/login/loginSlice";
import { IAppointmentForm } from "../../../../../../hooks/useSchedulingForm";
import VehicleIcon from "../../../../../../components/VehicleIcon";
import useDarkMode from "../../../../../../hooks/useDarkMode";

interface ISuccessDialogProps {
  open: boolean;
  appointmentForm: IAppointmentForm | undefined;
  routeWhenClose?: string;
}

const SuccessDialog = ({
  open,
  appointmentForm,
  routeWhenClose,
}: ISuccessDialogProps) => {
  const userProfile = useSelector(getUserProfile);
  const intl = useIntl();
  const [scheduleDate, setScheduleDate] = useState<Date>();
  const [user, setUser] = useState<IUserProfile>();
  const isDarkMode = useDarkMode();

  useEffect(() => {
    const fetchData = async () => {
      const userId = userProfile?.sub?.split(":")[1];
      if (userId) {
        const user = await fetchUser(userId);
        setUser(user);
      }
    };

    if (appointmentForm?.dateAndConsultant?.scheduleDate)
      setScheduleDate(
        new Date(appointmentForm?.dateAndConsultant?.scheduleDate?.getTime())
      );

    fetchData();
  }, [open]);

  return (
    <Dialog
      show={open}
      title={intl.formatMessage({ id: "intl-msg:dealership.successDialog.title" })}
      onClose={() => (window.location.href = routeWhenClose ?? "/dealership")}
      bsSize="sm"
      body={
        <div>
          <div
            style={{
              backgroundColor: isDarkMode ? "#0f2e21" : "#ECFDF3",
              borderColor: isDarkMode ? "#166534" : "#ABEFC6",
            }}
            className="border rounded padding-15"
          >
            <div className="flex">
              <div className="text-center w-14">
                <span className="rioglyph rioglyph-ok bg-success text-[#ffffff] rounded-circle padding-2" />
              </div>
              <div className="flex-grow">
                <div className="font-bold">
                  {intl.formatMessage({ id: "intl-msg:dealership.successDialog.headline" })}
                </div>
                <div>
                  {intl.formatMessage({ id: "intl-msg:dealership.successDialog.managerNotified" })}
                </div>
              </div>
            </div>
          </div>

          <hr />

          <div className="flex items-start gap-4 padding-top-10 padding-bottom-10">
            <VehicleIcon 
              chassis={appointmentForm?.assetsAndDrivers?.asset?.chassis} 
              size={80}
            />

            <div className="flex-1 padding-left-10">
              <p className="text-color-black text-bold text-size-16 m-0">
                {appointmentForm?.assetsAndDrivers?.asset?.name}
              </p>
              <p className="text-color-dark text-size-16 m-0">
                {intl.formatMessage({ id: "intl-msg:dealership.successDialog.chassis" }, { chassis: appointmentForm?.assetsAndDrivers?.asset?.chassis ?? "" })}
              </p>
            </div>
          </div>

          <hr />

          <div className="flex items-start gap-4">
            <div className="padding-right-25 border dark:border-neutral-700 border-right-only">
              <div className="text-size-16 text-bold">{intl.formatMessage({ id: "intl-msg:dealership.successDialog.requester" })}</div>
              <div
                className="text-size-16 text-color-dark"
                style={{ lineHeight: "34px" }}
              >
                {user?.name ?? "-"}
              </div>
            </div>

            <div className="padding-left-10 flex-grow border dark:border-neutral-700 border-right-only">
              <div className="text-size-16 text-bold">{intl.formatMessage({ id: "intl-msg:schedulingSummary.date" })}</div>
              <div className="flex" style={{ lineHeight: "34px" }}>
                <span className="rioglyph rioglyph-calendar" />{" "}
                {scheduleDate?.toLocaleDateString(intl.locale, {
                  day: "2-digit",
                  month: "long",
                })}
              </div>
            </div>

            <div className="width-100 padding-left-15">
              <div className="text-size-16 text-bold">{intl.formatMessage({ id: "intl-msg:dealership.successDialog.time" })}</div>
              <div className="text-size-16" style={{ lineHeight: "34px" }}>
                {intl.formatMessage({ id: "intl-msg:dealership.successDialog.timeValue" }, {
                  time: scheduleDate?.toLocaleTimeString(intl.locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                  }) ?? "",
                })}
              </div>
            </div>
          </div>
        </div>
      }
      footer={
        <button
          type="button"
          className="btn btn-primary btn-link"
          onClick={() =>
            (window.location.href = routeWhenClose ?? "/dealership")
          }
        >
          {intl.formatMessage({ id: "intl-msg:appointment.step3.close" })}
        </button>
      }
    />
  );
};

export default SuccessDialog;
